/**
 * The server-search engine behind the async pickers (ME3a).
 *
 * Extracted VERBATIM from ``AsyncCombobox`` when the multi-select
 * sibling arrived, because the hard parts here are not "search a list" —
 * they are four specific bugs that were found the expensive way:
 *
 *   - **Stale responses.** A monotonic counter drops a slow early query
 *     so it cannot overwrite a newer one's results.
 *   - **Duplicate rows.** "Mehr laden" de-duplicates by id, so a row
 *     shifting between pages cannot appear twice.
 *   - **Query reset.** A changed query discards the previous pages
 *     rather than appending to them.
 *   - **Debounce.** 300 ms on typing; the first load on open is NOT
 *     debounced, so the list is not empty for a third of a second.
 *
 * Two copies of that would mean the next fix lands in one of them. The
 * behaviour is unchanged from the single-select original — this is a
 * move, not a rewrite.
 *
 * The hook owns only the DATA. Selection, trigger text and markup stay
 * with each component, because that is exactly where single- and
 * multi-select differ.
 */
import * as React from "react";

const DEBOUNCE_MS = 300;
export const DEFAULT_PAGE_SIZE = 20;

/**
 * @param {object}   opts
 * @param {boolean}  opts.open       popover open state — drives loading
 * @param {function} opts.fetchPage  ({query, offset, limit}) => {items, total}
 * @param {function} opts.getItemId  item => stable id
 * @param {number}   opts.pageSize
 * @param {string}   opts.errorText  fallback message for a thrown error
 */
export default function useAsyncPagedSearch({
  open,
  fetchPage,
  getItemId,
  pageSize = DEFAULT_PAGE_SIZE,
  errorText,
}) {
  const [query, setQuery] = React.useState("");
  const [debouncedQuery, setDebouncedQuery] = React.useState("");
  const [items, setItems] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Monotonic request id — only the newest response may write state.
  const seqRef = React.useRef(0);

  // Debounce typing. The FIRST load (empty query on open) is not
  // debounced — it fires from the open effect below.
  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  const load = React.useCallback(
    async ({ q, offset, append }) => {
      const seq = ++seqRef.current;
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);
      try {
        const data = await fetchPage({ query: q, offset, limit: pageSize });
        if (seq !== seqRef.current) return; // a newer request already won
        const page = Array.isArray(data?.items) ? data.items : [];
        const nextTotal =
          typeof data?.total === "number" ? data.total : page.length;
        setTotal(nextTotal);
        setItems((prev) => {
          const base = append ? prev : [];
          const seen = new Set(base.map((it) => String(getItemId(it))));
          const merged = [...base];
          page.forEach((it) => {
            const id = String(getItemId(it));
            if (seen.has(id)) return; // shifting window → never duplicate
            seen.add(id);
            merged.push(it);
          });
          return merged;
        });
      } catch (err) {
        if (seq !== seqRef.current) return;
        setError(err?.message || errorText);
      } finally {
        if (seq === seqRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [fetchPage, getItemId, pageSize, errorText]
  );

  // Load page 1 when the popover opens, and whenever the debounced
  // query changes while it is open. A new query discards old pages.
  React.useEffect(() => {
    if (!open) return;
    load({ q: debouncedQuery, offset: 0, append: false });
  }, [open, debouncedQuery, load]);

  // Reset the transient search state when the popover closes so the
  // next open starts from the top instead of a stale query.
  React.useEffect(() => {
    if (open) return;
    setQuery("");
    setDebouncedQuery("");
    setItems([]);
    setTotal(0);
    setError(null);
  }, [open]);

  const reload = React.useCallback(
    () => load({ q: debouncedQuery, offset: 0, append: false }),
    [load, debouncedQuery]
  );

  const loadMore = React.useCallback(
    () => load({ q: debouncedQuery, offset: items.length, append: true }),
    [load, debouncedQuery, items.length]
  );

  return {
    query,
    setQuery,
    items,
    total,
    loading,
    loadingMore,
    error,
    hasMore: items.length < total,
    reload,
    loadMore,
  };
}
